import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "../shared/layout";
import {
  getWaitingList,
  getWaitingListStats,
  sendBulkEmailToWaitingList,
  deleteFromWaitingList,
} from "@/data";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import {
  Mail,
  Trash2,
  Users,
  TrendingUp,
  Calendar,
  Send,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

export function WaitingListScreen() {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState([]);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({ subject: "", message: "" });

  const { data, isLoading } = useQuery({
    queryKey: ["waitingList"],
    queryFn: getWaitingList,
  });

  const { data: stats } = useQuery({
    queryKey: ["waitingListStats"],
    queryFn: getWaitingListStats,
  });

  const sendEmailMutation = useMutation({
    mutationFn: ({ subject, message, selectedIds }) =>
      sendBulkEmailToWaitingList(subject, message, selectedIds),
    onSuccess: () => {
      queryClient.invalidateQueries(["waitingList"]);
      setEmailDialog(false);
      setEmailData({ subject: "", message: "" });
      setSelectedIds([]);
      toast({
        title: "Başarılı",
        description: "Email gönderildi",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Email gönderilemedi",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFromWaitingList,
    onSuccess: () => {
      queryClient.invalidateQueries(["waitingList"]);
      toast({
        title: "Başarılı",
        description: "Kayıt silindi",
        variant: "success",
      });
    },
  });

  const handleSendEmail = () => {
    sendEmailMutation.mutate({
      subject: emailData.subject,
      message: emailData.message,
      selectedIds: selectedIds.length > 0 ? selectedIds : undefined,
    });
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === waitingList.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(waitingList.map(e => e.id));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: tr });
    } catch (error) {
      return dateString;
    }
  };

  const waitingList = data?.data?.data || [];
  const statsData = stats?.data?.data || {};

  return (
    <AdminLayout
      title="Bekleme Listesi"
      description="Bekleme listesine kaydolan kullanıcıları yönetin"
      loading={isLoading}
    >
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsData.totalEntries || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bugün</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsData.todayEntries || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bu Hafta</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsData.thisWeekEntries || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bu Ay</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsData.thisMonthEntries || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.length > 0 && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Mail className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-900">
                {selectedIds.length} kişi seçildi
              </span>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => setEmailDialog(true)}
              >
                <Send className="mr-2 h-4 w-4" />
                Toplu Email Gönder
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds([])}
              >
                İptal
              </Button>
            </div>
          </div>
        )}

        {/* Main Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Kayıtlı Emailler ({waitingList.length})</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEmailDialog(true)}
              >
                <Mail className="mr-2 h-4 w-4" />
                Hepsine Email Gönder
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {waitingList.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="mx-auto h-12 w-12 mb-4 text-gray-300" />
                <p>Henüz kayıt bulunmuyor.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Checkbox
                        checked={selectedIds.length === waitingList.length}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>İsim</TableHead>
                    <TableHead>Kayıt Tarihi</TableHead>
                    <TableHead>İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {waitingList.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(entry.id)}
                          onCheckedChange={() => toggleSelection(entry.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{entry.email}</TableCell>
                      <TableCell>{entry.name || "—"}</TableCell>
                      <TableCell>{formatDate(entry.createdAt)}</TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Kaydı Sil</AlertDialogTitle>
                              <AlertDialogDescription>
                                Bu kaydı silmek istediğinizden emin misiniz?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>İptal</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMutation.mutate(entry.id)}
                              >
                                Sil
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Dialog */}
      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Toplu Email Gönder</DialogTitle>
            <DialogDescription>
              {selectedIds.length > 0
                ? `${selectedIds.length} kişiye email gönderilecek`
                : `Tüm bekleme listesine (${waitingList.length} kişi) email gönderilecek`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Konu *</Label>
              <Input
                id="subject"
                placeholder="Email konusu..."
                value={emailData.subject}
                onChange={(e) =>
                  setEmailData({ ...emailData, subject: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Mesaj *</Label>
              <Textarea
                id="message"
                placeholder="Email içeriği... ({{name}} ile isim kullanabilirsiniz)"
                value={emailData.message}
                onChange={(e) =>
                  setEmailData({ ...emailData, message: e.target.value })
                }
                rows={8}
              />
              <p className="text-xs text-gray-500">
                İpucu: Mesajda {"{"}{"{"} name {"}"}{"}"}  kullanarak kişinin ismini ekleyebilirsiniz
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEmailDialog(false);
                setEmailData({ subject: "", message: "" });
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={
                !emailData.subject ||
                !emailData.message ||
                sendEmailMutation.isLoading
              }
            >
              <Send className="mr-2 h-4 w-4" />
              {sendEmailMutation.isLoading ? "Gönderiliyor..." : "Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
